/**
 * Lesson text is typed in the CRM as plain text and stored as the small HTML the student platform
 * renders (`Lesson.content`, sanitised there with DOMPurify). The rules are the few a teacher needs:
 *
 *   a blank line starts a new paragraph, a line break stays a line break,
 *   lines starting with "- " make a bulleted list, "1. " a numbered one, "## " a heading,
 *   **bold**, and a web address becomes a link.
 *
 * htmlToText reads that HTML back. HTML it cannot read back exactly (a table, a picture, styles from
 * an old import) comes back with `plain: false`, and the editor then edits the HTML itself.
 */

const BULLET_RE = /^\s*[-•*]\s+/;
const NUMBERED_RE = /^\s*\d+[.)]\s+/;
const HEADING_RE = /^\s*##\s+/;
// A web address in escaped text: up to a space, a tag or an escaped quote. Punctuation that ends the
// sentence after it is not part of it.
const URL_RE = /https?:\/\/(?:(?!&quot;|&#39;|&gt;)[^\s<])+/g;
const TRAILING_PUNCT_RE = /[.,;:!?)»]+$/;

const escapeHtml = (s) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

function inlineToHtml(line) {
  return escapeHtml(line.trim())
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(URL_RE, (match) => {
      const url = match.replace(TRAILING_PUNCT_RE, '');
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>${match.slice(url.length)}`;
    });
}

/** Plain text as the teacher typed it → the lesson's HTML ('' for nothing). */
export function textToHtml(text) {
  const blocks = String(text || '')
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((block) => block.split('\n').filter((line) => line.trim() !== ''))
    .filter((lines) => lines.length > 0);

  return blocks.map((lines) => {
    if (lines.every((l) => BULLET_RE.test(l))) {
      return `<ul>${lines.map((l) => `<li>${inlineToHtml(l.replace(BULLET_RE, ''))}</li>`).join('')}</ul>`;
    }
    if (lines.every((l) => NUMBERED_RE.test(l))) {
      return `<ol>${lines.map((l) => `<li>${inlineToHtml(l.replace(NUMBERED_RE, ''))}</li>`).join('')}</ol>`;
    }
    if (lines.length === 1 && HEADING_RE.test(lines[0])) {
      return `<h3>${inlineToHtml(lines[0].replace(HEADING_RE, ''))}</h3>`;
    }
    return `<p>${lines.map(inlineToHtml).join('<br>')}</p>`;
  }).join('');
}

class NotPlain extends Error {}

// Text inside a block: text, <br>, <strong>/<b> around text, and a link whose text is its address.
function inlineToText(node) {
  let out = '';
  node.childNodes.forEach((child) => {
    if (child.nodeType === 3) {
      out += child.textContent.replace(/\s*\n\s*/g, ' ');
      return;
    }
    if (child.nodeType !== 1) return; // comments
    const tag = child.tagName;
    if (tag === 'BR') {
      out += '\n';
    } else if (tag === 'STRONG' || tag === 'B') {
      if (child.querySelector('*')) throw new NotPlain();
      out += `**${child.textContent}**`;
    } else if (tag === 'A') {
      const href = child.getAttribute('href') || '';
      if (child.querySelector('*') || child.textContent.trim() !== href) throw new NotPlain();
      out += href;
    } else {
      throw new NotPlain();
    }
  });
  return out;
}

/** The lesson's HTML → { text, plain }. `plain: false` means the text editor would lose something. */
export function htmlToText(html) {
  const source = String(html || '').trim();
  if (!source) return { text: '', plain: true };
  if (typeof DOMParser === 'undefined') return { text: '', plain: false };

  const doc = new DOMParser().parseFromString(source, 'text/html');
  const blocks = [];
  try {
    doc.body.childNodes.forEach((node) => {
      if (node.nodeType === 3) {
        if (node.textContent.trim()) blocks.push(node.textContent.trim());
        return;
      }
      if (node.nodeType !== 1) return;
      const tag = node.tagName;
      if (tag === 'P') {
        blocks.push(inlineToText(node).split('\n').map((l) => l.trim()).join('\n').trim());
      } else if (tag === 'UL' || tag === 'OL') {
        const items = [];
        node.childNodes.forEach((li) => {
          if (li.nodeType === 3 && !li.textContent.trim()) return;
          if (li.nodeType !== 1 || li.tagName !== 'LI') throw new NotPlain();
          const line = inlineToText(li).trim();
          if (line.includes('\n')) throw new NotPlain();
          items.push(tag === 'UL' ? `- ${line}` : `${items.length + 1}. ${line}`);
        });
        blocks.push(items.join('\n'));
      } else if (tag === 'H2' || tag === 'H3' || tag === 'H4') {
        blocks.push(`## ${inlineToText(node).trim()}`);
      } else {
        throw new NotPlain();
      }
    });
  } catch (err) {
    if (err instanceof NotPlain) return { text: '', plain: false };
    throw err;
  }
  return { text: blocks.filter(Boolean).join('\n\n'), plain: true };
}
